// analytics/components/charts/ChartFactory.tsx
import React, { useContext } from 'react';
import { LocalizeContext, LocalizeContextProps } from 'react-localize-redux';
import { Card, CardHeader, CardContent, Grid } from '@mui/material';
import BarChart from './BarChart';
import LineChart from './LineChart';
import PieChart from './PieChart';
import DoughnutChart from './DoughnutChart';
import GaugeChart from './GaugeChart';
import PlaceholderChart from '../PlaceholderChart';
import { AnalyticsChartResponse, ChartType, PayerCategory } from '../types';
import {
  isBillingByServiceCategoryResponse,
  isPatientCategoryBillingResponse,
} from '../utils/statsResponse';
import DemographicChart from './DemographicsCharts';
import DataValue from './DataValue';
// import PatientBarChart from '../../../dashboards/Analytics/PatientsBarChart';
import { IDepartment } from '../../../../constants/types';
import PatientBarChart from './PatientsBarChart';
import MultiChart from './MultiChart';
import ProductivityTable from './ProductivityTable';
import AppointmentStatsChart from './AppointmentStatsChart';
import AppointmentOutcomeSection from './AppointmentOutcomeSection';
import TypeDataTable from './TypeDataTable';
import AppointmentTypeChart from './TypeDataChart';
import WaitTimeSection from './WaitTimeSection';
import DiagnosisTable from './DiagnosisTable';
import InpatientDischargeDiagnosisTable from './InpatientDischargeDiagnosisTable';
import InsuranceTopTable from './InsuranceTopTable';
import CategoryBillingTable from './CategoryBillingTable';
import PatientCategoryService from './PatientCategoryTable';
import SurgeryKpiSection from './SurgeryKpiSection';
import SurgeryProcedureTable from './SurgeryProcedureTable';
import SurgerySurgeonTable from './SurgerySurgeonTable';
import SurgeryPipelineStatusChart from './SurgeryPipelineStatusChart';

export interface ChartFactoryProps {
  type: ChartType;
  title: string;
  data: AnalyticsChartResponse;
  id: string;
  departments:IDepartment[];
  departmentSelected:IDepartment | null;
  startDate?: number | null;
  endDate?: number | null;
  // SHER-853: money/unit/period presentation, declared per chart in config/analyticsCharts.ts.
  // Forwarded verbatim to MultiChart; every non-finances caller leaves them undefined and
  // gets exactly the rendering it had before.
  money?: boolean;
  currency?: string;
  totalUnitId?: string;
  markIncompletePeriod?: boolean;
  topN?: number;
  // SHER-899: which payer categories a Top-5 table reports on, declared per chart row.
  payerCategories?: PayerCategory[];
  // SHER-992: carried from the card's SourceState for the demographic card, which is the
  // only one whose population can arrive incomplete. Undefined everywhere else.
  counted?: number;
  expected?: number;
}

interface BarChartProps{
  series: {
      name: string,
      count: number[]
  }[],
  title:Element,
  categories:Date[]
}

const ChartFactory: React.FC<ChartFactoryProps> = ({ type, title: titleId, data: originalData, id, departments, departmentSelected, startDate, endDate, money, currency, totalUnitId, markIncompletePeriod, topN, payerCategories, counted, expected }) => {

  // Chart titles arrive from config/analyticsCharts.ts as translation ids and are resolved
  // here, once, for every tab. They used to be English prose written straight into that
  // config and passed to CardHeader, so a French tenant read an English card header wrapped
  // around a French table, and switching language moved the table but not the header.
  //
  // Resolved at this level rather than inside each chart component because ChartFactory is
  // the single point all seven tabs go through: the ~20 components below keep receiving a
  // plain string and none of them had to learn about localisation.
  //
  // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from the
  // package's typings. See analyticsTranslate.sher828.test.tsx.
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);
  const title = translate(titleId) as string;

  // If no data, show placeholder
  if (!originalData) {
    console.log('No data in chart factory', title, type, originalData);
    return <PlaceholderChart title={title} type={type} data={title}  />;
  }

  const data = originalData.chartData ? originalData.chartData : originalData;

  const categories = data.query?.map((item) => item.date) || [];
  const seriesDepartment = {
    name: title,
    count: data.trend?.totals || []
  };
  
  console.log('categories', categories, seriesDepartment);

  const presentation = { money, currency, totalUnitId, markIncompletePeriod, topN };

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return <BarChart data={data} title={title} categories={categories} series={seriesDepartment} type={type} />;
      case 'demographic':
        return <DemographicChart data={data} title={title} counted={counted} expected={expected} />;
      case 'data-value':
        return <DataValue data={data} title={title} type={type} value={'hopla'} size={'large'}/>;
      case 'multi-chart':
        return <MultiChart {...presentation} series={data?.series || []} categories={data?.categories || []} title={title} horizontal={true} type="bar"/>;
      case 'patient-bar':
        return <PatientBarChart departments={departments} departmentSelected={departmentSelected} data={data} statsPerDepartment={data} title={title}  />;
      case 'bar-chart':
          return <MultiChart {...presentation} series={data?.series || []} categories={data?.categories || []} title={title} horizontal={false} type="bar" total={data?.count} stacked={false}/>;
      case 'bar-stacked':
          return <MultiChart {...presentation} series={data?.series || []} categories={data?.categories || []} title={title} horizontal={true} type="bar" total={data?.count} stacked={true}/>;
      case 'radar':
          return <MultiChart {...presentation} series={data?.series || []} categories={data?.categories || []} title={title} horizontal={false} type="radar" total={data?.count} stacked={false}/>;
      case 'distribuited':
        return <MultiChart {...presentation} series={data?.series || []} categories={data?.categories || []} title={title} horizontal={false} type="bar" total={data?.count} stacked={true} ditributed={true}/>;
      case 'horizontal-bar':
        return <MultiChart {...presentation} series={data?.series || []} categories={data?.categories || []} title={title} horizontal={true} type="bar" total={data?.count}/>;

      case 'line':
        return <MultiChart {...presentation} series={data?.series || []} categories={data?.categories || []} title={title} horizontal={false} type="line" total={data?.count}/>;
      case 'pie':
        return <MultiChart {...presentation} series={data?.series || []} categories={data?.categories || []} title={title} horizontal={false} type="pie" total={data?.count}/>;
      case 'donut':
          return <MultiChart {...presentation} series={data?.series || []} categories={data?.categories || []} title={title} horizontal={false} type="donut" total={data?.count}/>;
      case 'appointment-stats':
            return <AppointmentStatsChart stats={data.stats} title={title} height={400}/>;
      case 'appointment-outcomes':
        return <AppointmentOutcomeSection data={originalData} title={title} />;
      case 'doughnut':
        return <DoughnutChart data={data} title={title} type={type} />;
      case 'gauge':
        return <GaugeChart data={data} title={title} type={type} />;
      case 'data-only':
        return <PlaceholderChart title={title} type={type} data={originalData}/>;
      case 'productivity':
        return <ProductivityTable data={data.data} title={title} type={type} stats={data.stats} department={data.department} appointmentTypes={data.appointmentTypes} showOptionalColumns={true}/>;
      case 'appointment-type-table':
        // Chart above table filled a full-width card twice over and pushed everything
        // below it off the screen. They carry the same figures in two readings, so they
        // sit side by side and the headers say which reading is which: the chart
        // normalises each department to 100%, the table counts appointments.
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <AppointmentTypeChart data={data.stats} appointmentTypes={data.appointmentTypes} title={`${title} (%)`} chartType="bar"
                  stacked={true}
                  horizontal={true} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TypeDataTable data={data.stats} appointmentTypes={data.appointmentTypes} title={`${title} (n)`} />
            </Grid>
          </Grid>
        );
      case 'wait-time':
        return <WaitTimeSection data={data} title={title} />;
      case 'diagnosis-table':
        return <DiagnosisTable data={data} title={title} />;
      case 'insurance-top-table':
        // `data`, not `originalData`: the unwrap above is a no-op for this source because it
        // yields a bare array, and using it keeps the one call-site expression the factory uses
        // everywhere else. Casting `originalData` to the row type instead does not overlap the
        // prop union and only trades a real type error for a suppressed one.
        return (
          <InsuranceTopTable
            insurers={data || []}
            payerCategories={payerCategories || []}
            title={title}
            limit={topN ?? 5}
          />
        );
      case 'discharge-diagnosis-table':
        // Same contract as the outpatient diagnosis table above: `useInpatientsData` resolves the
        // rows, so this reads `{ rows }` and needs no type guard. The failed-fetch case never
        // reaches here — `data` is null and the `!originalData` guard renders a placeholder.
        return <InpatientDischargeDiagnosisTable data={data} title={title} />;
      case 'category-billing-table':
        return <CategoryBillingTable data={isBillingByServiceCategoryResponse(originalData) ? originalData.stats : undefined} startDate={startDate} endDate={endDate} />;
      case 'patient-category-billing':
        // SHER-957: checked against what the endpoint actually answers, which is one row per BILL.
        // See `PatientCategoryBillingLine` for the contract and what is still open on SHER-939.
        return <PatientCategoryService data={isPatientCategoryBillingResponse(originalData) ? originalData.stats.lines : []} startDate={startDate} endDate={endDate} />;
      case 'surgery-kpi-section':
        return <SurgeryKpiSection totals={originalData} />;
      case 'surgery-procedure-table':
        return <SurgeryProcedureTable rows={data || []} title={title} />;
      case 'surgery-surgeon-table':
        return <SurgerySurgeonTable rows={data || []} title={title} />;
      case 'surgery-wait-distribution':
        return (
          <MultiChart
            {...presentation}
            series={data?.series || []}
            categories={data?.categories || []}
            title={title}
            horizontal={false}
            type="bar"
            total={data?.count}
          />
        );
      case 'surgery-pipeline-status':
        return <SurgeryPipelineStatusChart data={data} title={title} />;
      default:
        return <PlaceholderChart title={title} type={type} data={data}/>;
    }
  };

  return (
    <Card>
      {/* <CardHeader title={title} /> */}
      <CardContent>
        {renderChart()}
      </CardContent>
    </Card>
  );
};

export default ChartFactory;